import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { MessageService, SelectItem } from 'primeng';
import { EventService, TaskService, VehicleService } from '../../../../services';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ControlsOf } from '../../../../utils/controls-of';
import {
  JobScreenVehicleInformationTask,
  JobScreenVehicleInformationVehicle,
} from '../../../../interfaces/screen-specific/job-screen';
import { Message } from '../../../../classes/Message';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface EditVehicleForm {
  registrationNumber: string;
  make: string;
  model: string;
  fuelType: string;
  body: string;
  color: string;
  sizeEngine: number;
  vin: string;
  height: number;
  width: number;
  kerbWeight: number;
  // transmissionType: string;
}

@Component({
  templateUrl: './job-screen-edit-vehicle-dialog.component.html',
  styleUrls: ['./job-screen-edit-vehicle-dialog.component.scss'],
  selector: 'job-screen-edit-vehicle-dialog',
})
export class JobScreenEditVehicleDialog implements OnInit, OnDestroy {
  @Input() visible: boolean = false;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() vehicleUpdated: EventEmitter<void> = new EventEmitter<void>();

  @Input() taskId: number;

  vehicle: JobScreenVehicleInformationVehicle;

  public form: FormGroup<ControlsOf<EditVehicleForm>> = new FormGroup<ControlsOf<EditVehicleForm>>({
    registrationNumber: new FormControl(null, Validators.required),
    make: new FormControl(null, [Validators.required, Validators.minLength(2)]),
    model: new FormControl(null, Validators.required),
    fuelType: new FormControl(null, Validators.required),
    kerbWeight: new FormControl(null, [Validators.required, Validators.min(0)]),
    color: new FormControl(null),
    body: new FormControl(null),
    height: new FormControl(null),
    vin: new FormControl(null, this.vinConditionalValidator()),
    width: new FormControl(null),
    sizeEngine: new FormControl(null, [Validators.min(0)]),
  });

  private vinConditionalValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;

      const regNumber = control.parent.get('registrationNumber')?.value;
      const vin = control.value;

      if (
        (regNumber?.toUpperCase() === 'TBC' && !vin) ||
        (regNumber?.toUpperCase() === 'NOREG' && !vin)
      ) {
        return null;
      }

      if (!vin) {
        return { required: true };
      }

      const vinPattern = /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/;
      return vinPattern.test(vin) ? null : { pattern: true };
    };
  }

  fuelTypes: SelectItem[] = [];

  public isBusy: boolean = false;

  public isLoadingVehicle: boolean = false;

  private destroy$ = new Subject<void>();

  public get saveDisabled(): boolean {
    return this.isBusy || this.form.invalid || !this.form.dirty;
  }

  noKerbWeightFound: boolean = false;

  constructor(
    private messageService: MessageService,
    private eventService: EventService,
    private vehicleService: VehicleService,
    private taskService: TaskService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async ngOnInit() {
    await this.getFuelTypes();
    const regCtrl = this.form.get('registrationNumber')!;
    const vinCtrl = this.form.get('vin')!;

    regCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // re-run VIN validator because logic depends on registrationNumber
      vinCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  private async getFuelTypes() {
    this.fuelTypes.push({ value: null, label: 'Select' });
    const res = await this.vehicleService.GetFuelTypes();
    res.forEach((x) => {
      this.fuelTypes.push({ value: x, label: x });
    });
  }

  onShow() {
    this.noKerbWeightFound = false;
    this.isLoadingVehicle = true;
    this.taskService
      .getForComponent(this.taskId, 'job-screen-vehicle-information')
      .then((task: JobScreenVehicleInformationTask) => {
        this.vehicle = task.vehicle;
        this.form.reset(this.vehicle);
      })
      .finally(() => {
        this.isLoadingVehicle = false;
      });
  }

  async saveVehicle() {
    try {
      this.isBusy = true;
      await this.vehicleService.PatchVehicle(this.vehicle.id, this.form.getRawValue());
      this.messageService.add(Message.Success('Vehicle updated'));
      this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
      this.eventService.JobScreenEvent.emit({ event: 'vehicle-updated' });
      this.vehicleUpdated.emit();
    } catch (e) {
      this.messageService.add(Message.Error('Failed to update vehicle'));
    } finally {
      this.isBusy = false;
    }
  }

  async searchVehicle() {
    try {
      this.noKerbWeightFound = false;
      this.isBusy = this.isLoadingVehicle = true;
      const result = await this.vehicleService.GetByRegNum(
        this.form.controls.registrationNumber.value,
      );

      if (result.kerbWeight == 0) {
        this.noKerbWeightFound = true;
        result.kerbWeight = this.form.controls.kerbWeight.value;
      }

      this.form.patchValue(result);
      this.form.markAsDirty();
    } catch (e) {
      this.messageService.add(Message.Error('Vehicle search failed'));
    } finally {
      this.isBusy = this.isLoadingVehicle = false;
    }
  }
}
